import React from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { FormField, ViewSwitcher } from '../../ui';
import { parseCalendarYmd, toCalendarDate } from '../../../utils/calendarDate';
import ExportSectionRow from './ExportSectionRow';
import {
  DATE_MODES,
  dateNote,
  effectiveThroughDate,
  isSunday,
  lastWeekEnding,
} from './packageExportUtils';

// Typed-but-unparseable dates are kept as this marker so the field shows an error.
export const INVALID_DATE = 'invalid';

const toYmd = (date) => {
  if (date === null) return null;
  if (date instanceof Date && Number.isNaN(date.getTime())) return INVALID_DATE;
  return parseCalendarYmd(date);
};

const DateField = ({ id, label, value, onChange, error, width, disabled, shouldDisableDate }) => (
  <FormField label={label} error={error} sx={{ width }}>
    <DatePicker
      value={toCalendarDate(value)}
      onChange={(date) => onChange(toYmd(date))}
      inputFormat="MM/dd/yyyy"
      shouldDisableDate={shouldDisableDate}
      disabled={disabled}
      renderInput={(params) => (
        <TextField {...params} id={id} size="small" error={!!error} />
      )}
    />
  </FormField>
);

/**
 * Hours section: Current / Through date / Between dates, the date fields,
 * the "Last week ending" shortcut and the date note (plan §4.5).
 */
const ExportDateRangeSection = ({ settings, todayYmd, errors, onChange, disabled }) => {
  const theme = useTheme();
  const { ft } = theme.palette;
  const lastSunday = lastWeekEnding(todayYmd);
  const through = effectiveThroughDate(settings, todayYmd);
  const isLastWeek = through === lastSunday;

  return (
    <ExportSectionRow label="Hours" labelOffset={9}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <ViewSwitcher
          size="medium"
          ariaLabel="Hours date range"
          options={DATE_MODES}
          value={settings.mode}
          onChange={(mode) => onChange({ mode })}
          disabled={disabled}
        />

        {settings.mode === 'through' && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <DateField
              id="package-export-through-date"
              label="Through (Sunday)"
              value={through}
              onChange={(ymd) => onChange({ throughDate: ymd })}
              error={errors.through}
              width={180}
              disabled={disabled}
              shouldDisableDate={(date) => date.getDay() !== 0}
            />
            <Button
              variant="text"
              aria-pressed={isLastWeek}
              disabled={disabled}
              onClick={() => onChange({ throughDate: null })}
              startIcon={isLastWeek ? <CheckOutlined sx={{ fontSize: 18 }} /> : null}
              sx={{
                mt: '22px', // line up with the 40px field under its label
                height: 40,
                px: 1,
                fontSize: 14,
                fontWeight: 500,
                color: isLastWeek ? ft.neutrals.textSecondary : theme.palette.primary.main,
                '& .MuiButton-startIcon': { mr: 0.75, color: ft.neutrals.textSecondary },
              }}
            >
              Last week ending
            </Button>
          </Box>
        )}

        {settings.mode === 'between' && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <DateField
              id="package-export-start-date"
              label="Start date"
              value={settings.startDate}
              onChange={(ymd) => onChange({ startDate: ymd })}
              error={errors.start}
              width={168}
              disabled={disabled}
            />
            <DateField
              id="package-export-end-date"
              label="End date"
              value={settings.endDate}
              onChange={(ymd) => onChange({ endDate: ymd })}
              error={errors.end}
              width={168}
              disabled={disabled}
            />
          </Box>
        )}

        <Box
          role="note"
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            px: 1.5,
            py: 1,
            bgcolor: ft.primary.tint,
            border: `1px solid ${ft.primary.border}`,
            borderRadius: `${ft.radius.control}px`,
            color: ft.primary.dark,
          }}
        >
          <InfoOutlined sx={{ fontSize: 16, mt: '2px', flexShrink: 0 }} />
          <Typography component="span" sx={{ fontSize: 13, lineHeight: 1.45 }}>
            {settings.mode === 'through' && !isSunday(through)
              ? 'Pick a Sunday for the through date.'
              : dateNote(settings, todayYmd)}
          </Typography>
        </Box>
      </Box>
    </ExportSectionRow>
  );
};

export default ExportDateRangeSection;
